import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { useTeams } from '@/context/TeamContext';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit2, ClipboardList, Calendar, Check, X } from 'lucide-react';

function formatDueDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

function AssignmentForm({ initial, plays, teams, onSave, onCancel }) {
  const { t } = useLang();
  const [title, setTitle] = useState(initial?.title || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [dueDate, setDueDate] = useState(initial?.due_date || '');
  const [teamId, setTeamId] = useState(initial?.team_id || teams?.[0]?.id || '');
  const [selected, setSelected] = useState(new Set(initial?.play_ids || []));
  const [filter, setFilter] = useState('');

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const filtered = plays.filter(p =>
    !filter || p.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="bg-card-steel border-2 border-gold/40 rounded-lg p-5 mb-6" data-testid="assignment-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-h uppercase text-xl">{initial?.id ? t('assign_edit') : t('assign_create')}</h3>
        <button onClick={onCancel} className="text-gold hover:text-alert"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('assign_team') || 'Equipo'}</label>
          <select
            value={teamId}
            onChange={e => setTeamId(e.target.value)}
            data-testid="assignment-team"
            className="w-full mt-1 bg-navy-deep border border-steel rounded px-3 py-2 text-white"
          >
            {teams.map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('assign_title')}</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            data-testid="assignment-title"
            placeholder={t('assign_title_placeholder')}
            className="w-full mt-1 bg-navy-deep border border-steel rounded px-3 py-2 text-white"
            maxLength={120}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('assign_due')}</label>
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            data-testid="assignment-due"
            className="w-full mt-1 bg-navy-deep border border-steel rounded px-3 py-2 text-white"
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('assign_notes')}</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            data-testid="assignment-notes"
            placeholder={t('assign_notes_placeholder')}
            rows={2}
            className="w-full mt-1 bg-navy-deep border border-steel rounded px-3 py-2 text-white"
            maxLength={500}
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-gold font-mono">
            {t('assign_select_plays')} <span className="text-gray-400 normal-case">({selected.size})</span>
          </div>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={t('search') || 'Search'}
            className="bg-navy-deep border border-steel rounded px-3 py-1.5 text-sm text-white w-48"
          />
        </div>
        <div className="max-h-72 overflow-y-auto border border-gold/20 rounded-md divide-y divide-gold/10">
          {filtered.map(p => {
            const checked = selected.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                data-testid={`assignment-play-${p.id}`}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 transition ${checked ? 'bg-gold/10' : 'hover:bg-gold/5'}`}
              >
                <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-gold border-gold' : 'border-gold/40'}`}>
                  {checked && <Check className="w-3.5 h-3.5 text-navy" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gold/70">{p.category} · {p.difficulty}</div>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-gray-400">{t('assign_no_plays')}</div>
          )}
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-md border border-steel text-gray-300 hover:border-gold/60">{t('cancel')}</button>
        <button
          onClick={() => onSave({ team_id: teamId, title, notes, due_date: dueDate, play_ids: Array.from(selected) })}
          disabled={selected.size === 0 || !teamId}
          data-testid="assignment-save"
          className="px-5 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('save')}
        </button>
      </div>
    </div>
  );
}

export default function Assignments() {
  const { t } = useLang();
  const { user } = useAuth();
  const { teams, activeTeam, activeTeamId } = useTeams();
  const isCoach = user?.role === 'coach' || user?.role === 'admin';
  const [list, setList] = useState([]);
  const [plays, setPlays] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | assignment
  const [loading, setLoading] = useState(true);
  // Coach view filters the list by team — defaults to the active team so the
  // workflow matches the rest of the app, but a pill row lets coaches switch.
  const [scopeTeamId, setScopeTeamId] = useState(activeTeamId || null);

  useEffect(() => { if (activeTeamId && !scopeTeamId) setScopeTeamId(activeTeamId); }, [activeTeamId, scopeTeamId]);

  const load = async () => {
    setLoading(true);
    try {
      const tid = scopeTeamId || activeTeamId;
      const [a, p] = await Promise.all([
        api.get(`/assignments${tid ? `?team_id=${tid}` : ''}`),
        isCoach ? api.get('/plays') : Promise.resolve({ data: { plays: [] } }),
      ]);
      setList(a.data.assignments || []);
      setPlays(p.data.plays || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [activeTeamId, scopeTeamId]);

  const playMap = React.useMemo(() => {
    const m = {};
    for (const p of plays) m[p.id] = p;
    return m;
  }, [plays]);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.patch(`/assignments/${editing.id}`, data);
        toast.success(t('assign_updated'));
      } else {
        await api.post('/assignments', data);
        toast.success(t('assign_created'));
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm(t('assign_delete_confirm'))) return;
    try {
      await api.delete(`/assignments/${id}`);
      toast.success(t('assign_deleted'));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <Layout>
      <BackToDashboard />
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_assignments')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('assignments_h1')}</h1>
          {isCoach && teams.length > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 flex-wrap" data-testid="assignments-team-picker">
              <span className="text-xs font-mono uppercase tracking-widest text-gray-400">{t('team_choose') || 'Equipo'}:</span>
              {teams.map(tm => (
                <button
                  key={tm.id}
                  onClick={() => setScopeTeamId(tm.id)}
                  data-testid={`assignments-team-${tm.id}`}
                  className={`px-3 py-1 rounded-full text-xs font-h uppercase tracking-wider border ${
                    scopeTeamId === tm.id ? 'bg-gold text-navy border-gold' : 'border-gold/30 text-gold hover:bg-gold/10'
                  }`}
                >
                  {tm.name}
                </button>
              ))}
            </div>
          )}
          {!isCoach && activeTeam && (
            <p className="text-gray-400 text-sm mt-1">
              {t('team_active')}: <span className="text-gold">{activeTeam.name}</span>
            </p>
          )}
        </div>
        {isCoach && (
          <button
            onClick={() => setEditing({ team_id: scopeTeamId || activeTeamId })}
            data-testid="assignment-create-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
          >
            <Plus className="w-4 h-4" /> {t('assign_create')}
          </button>
        )}
      </div>

      {editing && (
        <AssignmentForm
          initial={editing}
          plays={plays}
          teams={teams}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {loading && <div className="text-gray-400">{t('loading')}…</div>}

      {!loading && list.length === 0 && (
        <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gold/30 rounded-lg">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-h uppercase tracking-wider text-lg">{t('assignments_empty')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map(a => {
          const due = a.due_date ? new Date(a.due_date) : null;
          const overdue = due && due < new Date();
          return (
          <div key={a.id} data-testid={`assignment-card-${a.id}`} className="bg-card-steel border-2 border-gold/30 rounded-lg p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-h uppercase text-lg truncate">{a.title || t('assignments_h1')}</h3>
                {a.due_date && (
                  <div className={`text-xs mt-1 inline-flex items-center gap-1 font-mono uppercase tracking-widest ${overdue ? 'text-alert' : 'text-gold'}`}>
                    <Calendar className="w-3 h-3" /> {t('assign_due')}: {formatDueDate(a.due_date)}
                    {overdue && <span className="ml-1">· {t('assign_overdue')}</span>}
                  </div>
                )}
              </div>
              {isCoach && (
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => setEditing(a)} className="p-2 rounded border border-gold/30 text-gold hover:bg-gold/10" title={t('edit')}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => del(a.id)} data-testid={`assignment-delete-${a.id}`} className="p-2 rounded border border-alert/30 text-alert hover:bg-alert/10" title={t('delete')}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            {a.notes && <p className="text-sm text-gray-300 mb-3">{a.notes}</p>}
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">
              {a.play_ids?.length || 0} {t('plays')}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(a.play_ids || []).map(pid => {
                const p = playMap[pid];
                return (
                  <Link
                    key={pid}
                    to={`/playbook/${pid}`}
                    className="text-xs px-2.5 py-1 rounded-full border border-gold/40 text-gold hover:bg-gold/10 truncate max-w-[200px]"
                  >
                    {p?.name || pid.slice(0, 6)}
                  </Link>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
    </Layout>
  );
}
